import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';

const Container = styled.div`
  height: 100vh;
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px); /* Account for progress bar */
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px); /* Extra bottom padding for mobile CTA access */
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 80px); /* Extra space to ensure CTA is scrollable + 20px buffer */
    height: 100vh;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 120px); /* Ensure content is tall enough to scroll */
  position: relative;
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 80px);
  }
`;

const DescriptionText = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: 40px;
  text-align: center;
  
  p {
    margin-bottom: ${theme.spacing.md};
    
    &:last-child {
      margin-bottom: ${theme.spacing.lg};
    }
  }
  
  /* Support for HTML tags */
  strong, b {
    font-weight: ${theme.typography.weights.semiBold};
    color: ${theme.colors.text};
  }
  
  em, i {
    font-style: italic;
  }
  
  /* Line breaks work naturally, no special CSS needed */
  
  @media screen and (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: ${theme.typography.sizes.body};
  }
`;

const CommunityGrid = styled.div`
  display: grid;
  gap: ${theme.spacing.lg};
  max-width: 800px;
  width: 100%;
  margin-top: 0;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 40px;
  padding: 0 ${theme.spacing.md};
  justify-items: center;
  align-self: center;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    gap: ${theme.spacing.md};
    padding: 0 ${theme.spacing.sm};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.sm};
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

const MemberCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.sm};
  animation: ${fadeInUp} 0.15s ease-out forwards;
  animation-delay: ${props => props.delay || 0}ms;
  opacity: 0;
  cursor: pointer;
  position: relative;
  
  &:active {
    transform: scale(0.95);
  }
`;

const MemberImage = styled.div`
  width: 80px;
  height: 80px;
  border-radius: ${theme.borderRadius.full};
  background: linear-gradient(135deg, ${theme.colors.doryLighterBlue} 0%, ${theme.colors.doryBlue} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 3px solid ${theme.colors.doryLighterBlue};
  box-shadow: ${theme.shadows.md};
  flex-shrink: 0;
  position: relative;
  
  /* Add a subtle inner shadow for depth */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: ${theme.borderRadius.full};
    box-shadow: inset 0 2px 4px rgba(64, 92, 115, 0.2);
    pointer-events: none;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    border-radius: ${theme.borderRadius.full};
    position: absolute;
    z-index: 1;
  }
  
  /* Fallback styling when no image - only show when there's no img child */
  &:not(:has(img))::after {
    content: '👤';
    font-size: 2rem;
    color: white;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 70px;
    height: 70px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 60px;
    height: 60px;
  }
`;

const MemberName = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.light};
  color: ${theme.colors.doryBlue};
  text-align: center;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes.xs};
    max-width: 80px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.typography.sizes.xs};
    max-width: 70px;
  }
`;

const CenteredMemberContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.lg};
  animation: fadeIn 0.3s ease-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const CenteredMemberImage = styled.div`
  width: 150px;
  height: 150px;
  border-radius: ${theme.borderRadius.full};
  background: linear-gradient(135deg, ${theme.colors.doryLighterBlue} 0%, ${theme.colors.doryBlue} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 4px solid ${theme.colors.doryBlue};
  box-shadow: ${theme.shadows.lg};
  position: relative;
  
  /* Add a subtle inner shadow for depth */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: ${theme.borderRadius.full};
    box-shadow: inset 0 2px 4px rgba(64, 92, 115, 0.2);
    pointer-events: none;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    border-radius: ${theme.borderRadius.full};
    position: absolute;
    z-index: 1;
  }
  
  /* Fallback styling when no image - only show when there's no img child */
  &:not(:has(img))::after {
    content: '👤';
    font-size: 4rem;
    color: white;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 120px;
    height: 120px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100px;
    height: 100px;
  }
`;

const CenteredMemberInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const CenteredMemberName = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.doryBlue};
  text-align: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.typography.sizes.h4};
  }
`;

const CenteredMemberTitle = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.doryGreen};
  text-align: center;
  font-weight: ${theme.typography.weights.medium};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.typography.sizes.small};
  }
`;


const MemberTitle = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  font-family: ${theme.typography.bodyFont};
  font-size: 0.7rem;
  color: black;
  text-align: center;
  font-weight: ${theme.typography.weights.medium};
  line-height: 1.2;
  padding: ${theme.spacing.xs};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 70px;
    height: 70px;
    font-size: 0.65rem;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 60px;
    height: 60px;
    font-size: 0.6rem;
  }
`;



const ContinueButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px; /* Ensure good touch target size */
  box-shadow: ${theme.shadows.sm};
  margin-top: 0;
  margin-bottom: 24px;
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  z-index: 10;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px; /* Larger touch target on mobile */
    padding: 1rem 1.5rem; /* More padding for better touch experience */
  }
  
  /* For very small screens, ensure text fits */
  @media (max-width: 320px) {
    font-size: 0.875rem;
    padding: 0.75rem 1rem 2.0rem 1rem;
    min-width: 150px;
  }
`;

const CommunityMembersScreen = ({ data, onNext, userProgress = {} }) => {
  const [members, setMembers] = useState([]);
  const [selectedMemberIndex, setSelectedMemberIndex] = useState(null);
  const [gridColumns, setGridColumns] = useState(4);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [displayedMembers, setDisplayedMembers] = useState([]);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [preloadedImages, setPreloadedImages] = useState(new Set());
  const observerRef = useRef(null);
  const imageRefs = useRef(new Map());

  // Preload images for faster display
  const preloadImage = useCallback((src) => {
    if (!src || preloadedImages.has(src)) return Promise.resolve();
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        setPreloadedImages(prev => new Set([...prev, src]));
        resolve();
      };
      img.onerror = reject;
      img.src = src;
    });
  }, [preloadedImages]);

  // Preload all member images in parallel
  const preloadAllImages = useCallback(async () => {
    const imagePromises = members
      .filter(member => member.image)
      .map(member => preloadImage(member.image));
    
    try {
      await Promise.all(imagePromises);
    } catch (error) {
      console.warn('Some images failed to preload:', error);
    }
  }, [members, preloadImage]);

  // Lazy loading with intersection observer
  const setupIntersectionObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            const src = img.dataset.src;
            if (src && !loadedImages.has(src)) {
              img.src = src;
              setLoadedImages(prev => new Set([...prev, src]));
            }
            observerRef.current.unobserve(img);
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before the image comes into view
        threshold: 0.1
      }
    );
  }, [loadedImages]);

  // Initialize members data from screen content or use default
  useEffect(() => {
    if (data.content?.members && Array.isArray(data.content.members)) {
      // Filter out members without names
      const validMembers = data.content.members.filter(member => member.name && member.name.trim() !== '');
      setMembers(validMembers);
    } else {
      // Default community members if none provided
      setMembers([
        { name: 'Sarah Chen', title: 'CEO', image: null },
        { name: 'Michael Rodriguez', title: 'CTO', image: null },
        { name: 'Emily Johnson', title: 'Designer', image: null },
        { name: 'David Kim', title: 'Developer', image: null },
        { name: 'Lisa Wang', title: 'Marketing', image: null },
        { name: 'James Wilson', title: 'Product', image: null },
        { name: 'Maria Garcia', title: 'Sales', image: null },
        { name: 'Alex Thompson', title: 'Support', image: null },
        { name: 'Rachel Brown', title: 'Operations', image: null },
        { name: 'Kevin Lee', title: 'Analytics', image: null },
        { name: 'Amanda Davis', title: 'Content', image: null },
        { name: 'Chris Taylor', title: 'Growth', image: null }
      ]);
    }
  }, [data.content?.members]);

  // Calculate optimal grid columns to maintain full grid layout
  useEffect(() => {
    const calculateGridColumns = () => {
      const containerWidth = Math.min(800, window.innerWidth - 64); // Max width minus padding
      const minItemWidth = window.innerWidth <= 768 ? 80 : window.innerWidth <= 1024 ? 100 : 120;
      const gap = window.innerWidth <= 768 ? 16 : window.innerWidth <= 1024 ? 24 : 32;
      
      // Calculate how many columns can fit
      const availableWidth = containerWidth + gap;
      const itemWidthWithGap = minItemWidth + gap;
      const maxColumns = Math.floor(availableWidth / itemWidthWithGap);
      
      // Always use the maximum columns that fit, regardless of member count
      // This ensures a full grid layout - if there aren't enough members,
      // the bottom row will be dropped automatically
      return Math.max(1, maxColumns);
    };

    const updateGridColumns = () => {
      setGridColumns(calculateGridColumns());
    };

    // Update on mount and resize
    updateGridColumns();
    window.addEventListener('resize', updateGridColumns);
    
    return () => window.removeEventListener('resize', updateGridColumns);
  }, []);

  // Always show all members in the grid
  useEffect(() => {
    if (members.length === 0 || gridColumns === 0) {
      setDisplayedMembers([]);
      return;
    }

    // Show all members regardless of grid layout completeness
    setDisplayedMembers(members);
  }, [members, gridColumns]);

  // Track screen size for name display
  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth <= parseInt(theme.breakpoints.tablet));
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleMemberClick = (index) => {
    if (selectedMemberIndex === index) {
      // Hide details if clicking the same member
      setSelectedMemberIndex(null);
    } else {
      // Show details in place of the same member's image
      setSelectedMemberIndex(index);
    }
  };

  const handleContinue = () => {
    onNext?.(data.key);
  };

  // Get formatted HTML content for the description text
  const htmlContent = formatTextContent(data.content?.text, userProgress);

  return (
    <Container>
      <Content>
        {/* Description text */}
        {htmlContent && (
          <DescriptionText dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}
        
        
        {/* Community members grid */}
        <CommunityGrid style={{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)` }}>
          {displayedMembers.map((member, index) => (
            <MemberCard 
              key={index} 
              delay={index * 25}
              onClick={() => handleMemberClick(index)}
            >
              {selectedMemberIndex === index ? (
                <MemberTitle>{member.title}</MemberTitle>
              ) : (
                <MemberImage>
                  {member.image && (
                    <img 
                      src={member.image} 
                      alt={member.name || 'Community Member'} 
                    />
                  )}
                </MemberImage>
              )}
              <MemberName>
                {isSmallScreen 
                  ? member.name?.split(' ')[0] || member.name
                  : member.name
                }
              </MemberName>
            </MemberCard>
          ))}
        </CommunityGrid>
        
        {/* CTA button */}
        <ContinueButton onClick={handleContinue}>
          {data.content?.ctaText || 'Continue'}
        </ContinueButton>
      </Content>
    </Container>
  );
};

export default CommunityMembersScreen;
